import type { IMenuDraft, TDraftQuestion } from '@/interfaces/IMenuImport';
import { orderedCategories } from './menu-draft';

/**
 * How a draft question reads on its card (plan §4.5.3): the text key, its
 * params and one option per answer. The texts come from i18n; only the params
 * (names, prices, currencies, lists) are built here, with the caller's
 * locale-aware formatters.
 */

export interface IQuestionFormat {
  /** A price in the workspace currency («$ 8.000»). */
  money(value: number): string;
  /** The plural currency name («pesos argentinos»). */
  currencyName(code: string): string;
  /** «a, b y c» in the locale. */
  list(items: string[]): string;
}

/** An option that opens an extra input before answering: the dish checklist or the new category name. */
export type TQuestionReveal = 'items' | 'categoryName';

export interface IQuestionOptionView {
  /** The `answer` value sent to the API. */
  answer: string;
  /** i18n key of the label; null when `label` is a plain text (a category name). */
  labelKey: string | null;
  label: string | null;
  labelParams: Record<string, string>;
  reveals: TQuestionReveal | null;
}

export interface IQuestionView {
  textKey: string;
  textParams: Record<string, string>;
  /** A two-form text: rendered with `$tc(textKey, textCount, textParams)`. */
  textCount?: number;
  options: IQuestionOptionView[];
}

/** Shown for a missing price inside a text («$ 8.000 y —»). */
export const MISSING_PRICE_TEXT = '—';

function keyOption(
  answer: string,
  labelKey: string,
  labelParams: Record<string, string> = {},
  reveals: TQuestionReveal | null = null
): IQuestionOptionView {
  return { answer, labelKey, label: null, labelParams, reveals };
}

function textOption(answer: string, label: string): IQuestionOptionView {
  return { answer, labelKey: null, label, labelParams: {}, reveals: null };
}

function assertNever(value: never): never {
  throw new Error(`Unknown question kind: ${JSON.stringify(value)}`);
}

export function questionView(
  question: TDraftQuestion,
  draft: IMenuDraft,
  fmt: IQuestionFormat
): IQuestionView {
  switch (question.kind) {
    case 'price_format':
      return {
        textKey: 'menuImport.questions.priceFormat.text',
        textParams: { priceRaw: question.params.priceRaw },
        options: [
          keyOption('yes', 'menuImport.questions.priceFormat.yes', {
            example: fmt.money(question.params.example),
          }),
          keyOption('no', 'menuImport.questions.priceFormat.no'),
        ],
      };

    case 'currency':
      return {
        textKey: 'menuImport.questions.currency.text',
        textParams: {
          detected: fmt.currencyName(question.params.detected),
          mark: question.params.mark,
        },
        options: [
          keyOption('detected', 'menuImport.questions.currency.option', {
            currency: fmt.currencyName(question.params.detected),
          }),
          keyOption('store', 'menuImport.questions.currency.option', {
            currency: fmt.currencyName(question.params.store),
          }),
        ],
      };

    case 'variant_merge':
      return {
        textKey: 'menuImport.questions.variantMerge.text',
        textParams: { names: fmt.list(question.params.names) },
        options: [
          keyOption('merge', 'menuImport.questions.variantMerge.yes'),
          keyOption('keep', 'menuImport.questions.variantMerge.no'),
        ],
      };

    case 'addon_scope':
      return {
        textKey: 'menuImport.questions.addonScope.text',
        textParams: {
          group: question.params.groupTitle,
          options: fmt.list(
            question.params.options.map((option) =>
              option.price > 0
                ? `${option.name} ${fmt.money(option.price)}`
                : option.name
            )
          ),
          category: question.params.categoryName,
        },
        options: [
          keyOption('all', 'menuImport.questions.addonScope.all'),
          keyOption(
            'some',
            'menuImport.questions.addonScope.some',
            {},
            'items'
          ),
        ],
      };

    case 'size_columns':
      return {
        textKey: 'menuImport.questions.sizeColumns.text',
        textParams: { columns: fmt.list(question.params.columns) },
        options: [
          keyOption('yes', 'menuImport.questions.sizeColumns.yes'),
          keyOption('no', 'menuImport.questions.sizeColumns.no'),
        ],
      };

    case 'combo':
      return {
        textKey: 'menuImport.questions.combo.text',
        textParams: { name: question.params.name },
        options: [
          keyOption('keep', 'menuImport.questions.combo.keep'),
          keyOption('split', 'menuImport.questions.combo.split'),
        ],
      };

    case 'category':
      return {
        textKey: 'menuImport.questions.category.text',
        textParams: { name: question.params.name },
        options: [
          ...orderedCategories(draft).map((category) =>
            textOption(category.id, category.name)
          ),
          keyOption(
            'new',
            'menuImport.questions.category.new',
            {},
            'categoryName'
          ),
        ],
      };

    case 'duplicate':
      return {
        textKey: 'menuImport.questions.duplicate.text',
        textParams: {
          name: question.params.name,
          prices: fmt.list(
            question.params.prices.map((price) =>
              price === null ? MISSING_PRICE_TEXT : fmt.money(price)
            )
          ),
        },
        options: [
          keyOption('keep_one', 'menuImport.questions.duplicate.yes'),
          keyOption('keep_both', 'menuImport.questions.duplicate.no'),
        ],
      };

    case 'payment_prices': {
      // The first dish still in the draft is the example; the options name
      // its two prices with their printed labels («Tarjeta: $ 20.000»).
      const pairs = question.params.pairs;
      const first = pairs.find((pair) => draft.items[pair.itemId]) ?? pairs[0];
      const high = first ? fmt.money(first.kept.price) : MISSING_PRICE_TEXT;
      const low = first ? fmt.money(first.other.price) : MISSING_PRICE_TEXT;
      /** «Tarjeta: $ 20.000», or the price alone when it has no label. */
      const labelled = (label: string, price: string) =>
        label.trim() === '' ? price : `${label}: ${price}`;
      return {
        textKey: 'menuImport.questions.paymentPrices.text',
        textParams: {
          name: first ? draft.items[first.itemId]?.name ?? '' : '',
          high,
          low,
        },
        options: [
          keyOption('higher', 'menuImport.questions.paymentPrices.higher', {
            price: first ? labelled(first.kept.label, high) : high,
          }),
          keyOption('lower', 'menuImport.questions.paymentPrices.lower', {
            price: first ? labelled(first.other.label, low) : low,
          }),
        ],
      };
    }

    case 'new_menu':
      // Update sessions: most dishes read are new, most of the menu is missing.
      return {
        textKey: 'menuImport.questions.newMenu.text',
        textParams: { count: String(question.params.count) },
        textCount: question.params.count,
        options: [
          keyOption('keep', 'menuImport.questions.newMenu.keep'),
          keyOption('hide', 'menuImport.questions.newMenu.hide'),
        ],
      };

    default:
      return assertNever(question);
  }
}
